import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type {
  FramingSpacingOriginPoint,
  OpeningLayoutBounds,
  PerimeterLayoutBounds,
} from "building-types/framing/framingLayoutTypes";
import type {
  BoundaryVertex,
  EdgeMetadata,
  FramingBoundaryWithMetadata,
} from "building-types/framing/framingMetaDataTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type { GeneratedWallBlockingMember } from "building-types/framing/wallFramingBlockingTypes";
import type { WallOpeningHorizontalBand } from "building-types/framing/wallFramingOpeningClusterTypes";
import type {
  BandFramingOptions,
  OpeningFramingConfigResolver,
} from "building-types/framing/wallFramingOpeningConfigTypes";
import type {
  DoorBottomPlateGap,
  ValidatedWallOpening,
  WallOpeningFramingContext,
} from "building-types/framing/wallFramingOpeningTypes";
import type { StudEndHit, StudEndTrimContext } from "building-types/framing/wallFramingStudEndTrimTypes";
import type {
  Framing,
  GeneratedFramedWallAssembly,
  WallFramingInput,
  WallOpeningFramingConfigGroup,
} from "building-types/framing/wallFramingTypes";
import { MIN_FRAMING_STUD_LENGTH, WallFramingType } from "building-types/framing/wallFramingTypes";
import type { Table } from "common/data/table";
import { getFramingProfileDescriptorById, lumberRailNew } from "doc/utils/lumberTools";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { COMPACTNESS_EPSILON, DIST_EPSILON, DIST_Z_FIGHT } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { offsetPolyLine2 } from "geom/offset";
import { planeIsOrientedCoplanarUvAligned, planeNewXZ, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2Extend,
  polyLine2Length,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Move } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec2IsBetweenUsingLengthBasedCalc } from "geom/vec2";
import { FramingGeneratorBase } from "../framingGeneratorBase";
import {
  collectBoundaryVertices,
  computeOpeningRanges,
  deriveOpeningLayoutBounds,
  derivePerimeterLayoutBounds,
  expandOpeningLayoutBoundsByBorder,
  framingSpacingSign,
  generateFramingSpacingOriginPoints,
} from "../framingLayoutUtils";
import {
  buildBlockingOpeningGaps,
  buildBlockingOpeningGapsFromBounds,
  createWallBlockingGenerationContext,
  generateWallBlockingMembers,
  resolveWallBlockingElevationOptions,
  layoutStartPerpFromOrigin,
} from "./wallFramingBlocking";
import { generateWallOpeningFramingForBand, groupWallOpeningsIntoHorizontalBands } from "./wallFramingOpeningClusters";
import { bandFramingOptionsFromInput, openingFramingConfigResolverFromInput } from "./wallFramingOpeningConfig";
import {
  countBottomAndTopPlates,
  computeStudVerticalExtents,
  splitBottomPlateEdgeAtDoorGaps,
  validateWallOpenings,
} from "./wallFramingOpenings";
import { resolveWallFramingTargetPlane, transformFramedWallAssemblyToPlane } from "./wallFramingPlaneTransform";
import {
  boundaryStudEndHits,
  buildStudEndClipPlanes,
  trimStudSegmentAtPerpendicularPlateEnds,
} from "./wallFramingStudEndTrim";
import { buildSlopedTopPlateClipPlanes } from "./wallFramingTopPlateClipPlanes";
import {
  classifyBoundaryEdgeType,
  offsetBoundaryRail2DForStack,
  offsetBoundaryRail3DForExteriorFace,
  openingHeaderExtrusionNormal,
  openingHeaderPlanDepthOffset,
  wallFramingExtrusionNormal,
} from "./wallFramingUtils";

const SLIGHTLY_LESS_THAN_HALF = 0.5 - DIST_EPSILON;
const DEFAULT_WALL_STUD_DIRECTION = Math.PI / 2;

interface GeneratedWallMember {
  readonly polyline: PolyLine2;
  readonly memberId: string;
  readonly profileDescriptor: FramingProfileDescriptor;
  readonly framingType: WallFramingType;
  readonly isBoundary: boolean;
  readonly boundaryStackIndex: number;
  readonly depthStackIndex: number;
  readonly boundaryEdgeIndex?: number;
  readonly startHit?: StudEndHit;
  readonly endHit?: StudEndHit;
  /** stable opening ids this member belongs to (opening members only). */
  readonly openingIds?: readonly string[];
  /** shared id linking the depth-stacked plies of a single physical header (HEADER members only). */
  readonly headerGroupId?: string;
}

/**
 * WallFramingGenerator for generating wall framing from a vertical-quad perimeter.
 */
export class WallFramingGenerator extends FramingGeneratorBase {
  private readonly input: WallFramingInput;
  private readonly framingComponents: Table<FramingComponentEntity>;

  private readonly defaultProfileDescriptor: FramingProfileDescriptor;
  private readonly framingStartPt: Vec2;
  private readonly framingDirectionVec: Vec2;
  private readonly framingPerpDirectionVec: Vec2;
  private readonly framingSpacing: number;

  constructor(
    input: WallFramingInput,
    framingComponents: Table<FramingComponentEntity>,
    memberIdStartOffset: number = 0,
  ) {
    super(memberIdStartOffset);
    this.input = input;
    this.framingComponents = framingComponents;

    const defaultProfile = input.framingMetadata.defaultFramingDescriptor;
    if (!defaultProfile) {
      throw new Error("WallFramingGenerator requires defaultFramingDescriptor on framingMetadata");
    }
    this.defaultProfileDescriptor = defaultProfile;

    const direction = input.framingMetadata.direction;
    const resolvedDirection = typeof direction === "number" ? direction : DEFAULT_WALL_STUD_DIRECTION;
    const { framingDirectionVec, framingPerpDirectionVec } = this.framingDirectionAndPerpDirectionVecs(
      resolvedDirection,
      input.perimeter.polyline,
    );
    this.framingDirectionVec = framingDirectionVec;
    this.framingPerpDirectionVec = framingPerpDirectionVec;
    this.framingSpacing = input.framingMetadata.spacing;
    this.framingStartPt = input.framingMetadata.framingStartPt;
  }

  generate(): GeneratedFramedWallAssembly {
    const perimeter = this.input.perimeter;
    const openings = this.input.openings ?? [];
    const roughWidthByOpeningIndex = new Map<number, number>();
    const rawOpeningBoundsByOpeningIndex = new Map<number, OpeningLayoutBounds>();

    const staticResolveOpeningConfig = openingFramingConfigResolverFromInput(
      this.input.openings,
      this.input.openingFramingConfigGroup,
    );

    const externalOpeningFramingResolver = this.input.resolveOpeningFramingConfig;

    const resolveOpeningConfig: OpeningFramingConfigResolver = (openingIndex) => {
      const opening = openings[openingIndex];
      const openingId = opening?.openingId;
      const roughWidth = roughWidthByOpeningIndex.get(openingIndex);
      if (externalOpeningFramingResolver && openingId !== undefined && roughWidth !== undefined) {
        const resolved = externalOpeningFramingResolver({ openingId, roughWidth });
        if (resolved) {
          return resolved;
        }
      }
      return staticResolveOpeningConfig(openingIndex);
    };

    /**this maybe a redundant check if we make sure a default is set on the input */
    const hasOpeningFramingConfig =
      externalOpeningFramingResolver !== undefined ||
      this.input.openingFramingConfigGroup !== undefined ||
      openings.some((opening) => opening.openingFramingConfigGroup !== undefined);

    const sortedVertices = collectBoundaryVertices(
      perimeter,
      openings,
      this.framingStartPt,
      this.framingPerpDirectionVec,
      this.framingDirectionVec,
    );

    const perimeterBounds = derivePerimeterLayoutBounds(sortedVertices);
    const spacingSign = framingSpacingSign(sortedVertices, this.framingStartPt, this.framingPerpDirectionVec);
    const plateThickness = this.defaultProfileDescriptor.height;
    const { bottomPlateCount, topPlateCount } = countBottomAndTopPlates(perimeter);
    const studEndTrimCtx = this.studEndTrimContext(bottomPlateCount, topPlateCount);

    let openingValidation = {
      validOpenings: [] as ValidatedWallOpening[],
      errors: [] as string[],
      doorBottomPlateGaps: [] as DoorBottomPlateGap[],
    };

    let openingContext: WallOpeningFramingContext | undefined;

    if (perimeterBounds && hasOpeningFramingConfig && openings.length > 0) {
      const { perpOpeningRanges, parallelOpeningRanges } = computeOpeningRanges(sortedVertices, openings.length);
      const rawOpeningBoundsList = perpOpeningRanges.map((perpRange) => {
        const parallelRange = parallelOpeningRanges.find((r) => r.openingIndex === perpRange.openingIndex);
        if (!parallelRange) {
          throw new Error(`Missing parallel range for opening ${perpRange.openingIndex}`);
        }
        return deriveOpeningLayoutBounds(perpRange.openingIndex, perpRange, parallelRange);
      });

      for (const bounds of rawOpeningBoundsList) {
        roughWidthByOpeningIndex.set(bounds.openingIndex, bounds.endPerp - bounds.startPerp);
        rawOpeningBoundsByOpeningIndex.set(bounds.openingIndex, bounds);
      }

      // Grow each opening to its rough opening (border clearance) before laying out framing. Width is
      // resolved from the raw width above, so header sizing already accounts for the border.
      const openingBoundsList = rawOpeningBoundsList.map((bounds) =>
        expandOpeningLayoutBoundsByBorder(bounds, resolveOpeningConfig(bounds.openingIndex)?.roughOpeningBorder ?? 0),
      );

      openingValidation = validateWallOpenings(openingBoundsList, perimeterBounds, plateThickness, bottomPlateCount, {
        perimeterPolyline: perimeter.polyline,
        openingPolylines: openings.map((opening) => opening.polyline),
      });

      const { studBottomParallel, studTopParallel } = computeStudVerticalExtents(
        perimeterBounds,
        plateThickness,
        bottomPlateCount,
        topPlateCount,
      );

      openingContext = {
        planeOrigin: this.framingStartPt,
        perpDirection: this.framingPerpDirectionVec,
        framingDirection: this.framingDirectionVec,
        perimeterBounds,
        studBottomParallel,
        studTopParallel,
        spacing: this.framingSpacing,
        spacingSign: framingSpacingSign(sortedVertices, this.framingStartPt, this.framingPerpDirectionVec),
        studHalfThickness: this.defaultProfileDescriptor.height / 2,
        sortedVertices,
        perimeter,
        studEndTrimCtx,
      };
    }

    const boundaryMembers = this.generateBoundaryFraming(
      perimeter,
      openingValidation.doorBottomPlateGaps,
      studEndTrimCtx,
    );
    const framedOpenings = openingValidation.validOpenings;
    const bandFramingOptions = bandFramingOptionsFromInput(this.input);
    const openingBands =
      perimeterBounds && hasOpeningFramingConfig && framedOpenings.length > 0
        ? groupWallOpeningsIntoHorizontalBands(
            framedOpenings,
            resolveOpeningConfig,
            this.defaultProfileDescriptor,
            this.framingComponents,
            plateThickness,
            bandFramingOptions,
          )
        : [];

    const mergedOpeningConfigByIndex = new Map<number, WallOpeningFramingConfigGroup>();
    if (externalOpeningFramingResolver) {
      for (const band of openingBands) {
        if (band.openings.length <= 1) {
          continue;
        }
        // Merged span from the RAW opening bounds (the resolver re-adds the border for header sizing).
        let mergedRawStart = Infinity;
        let mergedRawEnd = -Infinity;
        for (const bandOpening of band.openings) {
          const rawBounds = rawOpeningBoundsByOpeningIndex.get(bandOpening.openingIndex);
          if (rawBounds) {
            mergedRawStart = Math.min(mergedRawStart, rawBounds.startPerp);
            mergedRawEnd = Math.max(mergedRawEnd, rawBounds.endPerp);
          }
        }
        if (!Number.isFinite(mergedRawStart) || !Number.isFinite(mergedRawEnd)) {
          continue;
        }
        const mergedWidth = mergedRawEnd - mergedRawStart;
        for (const bandOpening of band.openings) {
          const openingId = openings[bandOpening.openingIndex]?.openingId;
          if (openingId === undefined) {
            continue;
          }
          const resolved = externalOpeningFramingResolver({ openingId, roughWidth: mergedWidth });
          if (resolved) {
            mergedOpeningConfigByIndex.set(bandOpening.openingIndex, resolved);
          }
        }
      }
    }
    /**if it is merged, use the merged config, otherwise use the static config */
    const resolveMemberOpeningConfig: OpeningFramingConfigResolver = (openingIndex) =>
      mergedOpeningConfigByIndex.get(openingIndex) ?? resolveOpeningConfig(openingIndex);

    const studs = this.generateStuds(perimeter, sortedVertices, openingBands, studEndTrimCtx);
    const openingMembers = this.generateOpeningMembers(
      openingBands,
      openingContext,
      resolveMemberOpeningConfig,
      bandFramingOptions,
    );
    const blockingMembers = this.generateBlockingMembers(
      sortedVertices,
      perimeterBounds,
      openingBands,
      framedOpenings,
      resolveOpeningConfig,
      bandFramingOptions,
      spacingSign,
    );

    const allMembers: GeneratedWallMember[] = [
      ...boundaryMembers,
      ...studs,
      ...openingMembers,
      ...blockingMembers.map((blocking) => ({
        polyline: blocking.polyline,
        memberId: this.generateMemberId(),
        profileDescriptor: blocking.profileDescriptor,
        framingType: blocking.framingType,
        isBoundary: false,
        boundaryStackIndex: 0,
        depthStackIndex: 0,
      })),
    ];
    const framing = this.computeStructuralMetadata(allMembers, studEndTrimCtx);

    const assembly: GeneratedFramedWallAssembly = {
      levelId: "debug-level",
      framing,
      openingFramingErrors: openingValidation.errors.length > 0 ? openingValidation.errors : undefined,
    };

    const canonicalPlane = planeNewXZ(0, 0, this.input.framingMetadata.elevation ?? 0);
    const targetPlane = resolveWallFramingTargetPlane(this.input, canonicalPlane);
    if (targetPlane && !planeIsOrientedCoplanarUvAligned(canonicalPlane, targetPlane)) {
      return transformFramedWallAssemblyToPlane(assembly, canonicalPlane, targetPlane);
    }

    return assembly;
  }

  private studEndTrimContext(bottomPlateCount: number, topPlateCount: number): StudEndTrimContext {
    return {
      perimeter: this.input.perimeter,
      profileDescriptor: this.defaultProfileDescriptor,
      elevation: this.input.framingMetadata.elevation ?? 0,
      bottomPlateCount,
      topPlateCount,
    };
  }

  private generateBoundaryFraming(
    perimeter: FramingBoundaryWithMetadata,
    doorBottomPlateGaps: DoorBottomPlateGap[],
    studEndTrimCtx: StudEndTrimContext,
  ): GeneratedWallMember[] {
    const members: GeneratedWallMember[] = [];

    for (let i = 0, l = polyLine2EdgeCount(perimeter.polyline); i < l; i++) {
      const edgeRail = polyLine2Edge(perimeter.polyline, i);

      const edgeMetadata: EdgeMetadata | undefined = perimeter.edgeMetadata?.[i];

      let finalProfileDescriptor = this.defaultProfileDescriptor;
      if (edgeMetadata?.framingConfig?.framingComponentId) {
        const overrideDescriptor = getFramingProfileDescriptorById(
          this.framingComponents,
          edgeMetadata.framingConfig.framingComponentId,
        );
        if (overrideDescriptor) {
          finalProfileDescriptor = overrideDescriptor;
        }
      }

      const framingConfig = edgeMetadata?.framingConfig;
      if (framingConfig?.disabled) {
        continue;
      }

      const framingType = classifyBoundaryEdgeType(perimeter.polyline, i);
      const framingCount = Math.max(1, framingConfig?.count ?? 1);

      const edgeRails =
        framingType === WallFramingType.BOTTOM_PLATE && doorBottomPlateGaps.length > 0
          ? splitBottomPlateEdgeAtDoorGaps(
              edgeRail,
              doorBottomPlateGaps,
              this.framingStartPt,
              this.framingPerpDirectionVec,
              this.framingDirectionVec,
            )
          : [edgeRail];

      for (const rail of edgeRails) {
        for (let idx = 0; idx < framingCount; idx++) {
          const rail2D = offsetBoundaryRail2DForStack(rail, framingType, finalProfileDescriptor, idx);

          let polyline = rail2D;
          let startHit: StudEndHit | undefined;
          let endHit: StudEndHit | undefined;

          if (framingType === WallFramingType.STANDARD_STUD) {
            const hits = boundaryStudEndHits(perimeter.polyline, i);
            startHit = hits.startHit;
            endHit = hits.endHit;
            polyline = trimStudSegmentAtPerpendicularPlateEnds(rail2D, startHit, endHit, studEndTrimCtx);
          }

          members.push({
            polyline,
            memberId: this.generateMemberId(),
            profileDescriptor: finalProfileDescriptor,
            framingType,
            isBoundary: true,
            boundaryStackIndex: idx,
            depthStackIndex: 0,
            boundaryEdgeIndex: i,
            startHit,
            endHit,
          });
        }
      }
    }

    return members;
  }

  private generateOpeningMembers(
    openingBands: WallOpeningHorizontalBand[],
    openingContext: WallOpeningFramingContext | undefined,
    resolveOpeningConfig: OpeningFramingConfigResolver,
    bandFramingOptions: BandFramingOptions,
  ): GeneratedWallMember[] {
    if (!openingContext || openingBands.length === 0) {
      return [];
    }

    const members: GeneratedWallMember[] = [];
    for (const band of openingBands) {
      const openingMembers = generateWallOpeningFramingForBand(
        band,
        resolveOpeningConfig,
        openingContext,
        this.framingComponents,
        this.defaultProfileDescriptor,
        bandFramingOptions,
      );
      const openings = this.input.openings ?? [];
      for (const member of openingMembers) {
        //map the opening indices to the opening ids
        const openingIds = (member.openingIndices ?? [])
          .map((openingIndex) => openings[openingIndex]?.openingId)
          .filter((openingId): openingId is string => openingId !== undefined);
        members.push({
          polyline: member.polyline,
          memberId: this.generateMemberId(),
          profileDescriptor: member.profileDescriptor,
          framingType: member.framingType,
          isBoundary: false,
          boundaryStackIndex: 0,
          depthStackIndex: member.depthStackIndex,
          startHit: member.startHit,
          endHit: member.endHit,
          openingIds: openingIds.length > 0 ? openingIds : undefined,
          headerGroupId: member.headerGroupId,
        });
      }
    }
    return members;
  }

  private generateBlockingMembers(
    sortedVertices: BoundaryVertex[],
    perimeterBounds: PerimeterLayoutBounds | undefined,
    openingBands: WallOpeningHorizontalBand[],
    validOpenings: ValidatedWallOpening[],
    resolveOpeningConfig: OpeningFramingConfigResolver,
    bandFramingOptions: BandFramingOptions,
    spacingSign: 1 | -1,
  ): GeneratedWallBlockingMember[] {
    if (this.input.framingMetadata.hasBlocking !== true || sortedVertices.length === 0 || !perimeterBounds) {
      return [];
    }

    const studThickness = this.defaultProfileDescriptor.height;
    const openingGaps =
      openingBands.length > 0
        ? buildBlockingOpeningGaps(openingBands, resolveOpeningConfig, bandFramingOptions, studThickness)
        : buildBlockingOpeningGapsFromBounds(
            validOpenings.map((opening) => opening.bounds),
            resolveOpeningConfig,
            studThickness,
          );

    const blockingCtx = createWallBlockingGenerationContext(
      this.framingStartPt,
      this.framingPerpDirectionVec,
      this.framingDirectionVec,
      perimeterBounds.bottomParallel,
      studThickness,
      this.framingSpacing,
      spacingSign,
      layoutStartPerpFromOrigin(this.framingStartPt, this.framingPerpDirectionVec, this.framingStartPt),
      sortedVertices[0].perpDistance,
      sortedVertices[sortedVertices.length - 1].perpDistance,
    );

    const elevationOptions = resolveWallBlockingElevationOptions(this.input.framingMetadata);
    return generateWallBlockingMembers(blockingCtx, this.defaultProfileDescriptor, openingGaps, elevationOptions);
  }

  private generateStuds(
    perimeter: FramingBoundaryWithMetadata,
    sortedVertices: BoundaryVertex[],
    openingBands: WallOpeningHorizontalBand[],
    studEndTrimCtx: StudEndTrimContext,
  ): GeneratedWallMember[] {
    if (sortedVertices.length === 0) {
      return [];
    }

    // One-sided OC from framingStartPt (see framingLayoutUtils); start should be a perimeter corner.
    const spacingSign = framingSpacingSign(sortedVertices, this.framingStartPt, this.framingPerpDirectionVec);
    const studHalfThickness = this.defaultProfileDescriptor.height / 2;

    const originPoints = generateFramingSpacingOriginPoints(
      sortedVertices,
      this.framingSpacing,
      this.framingStartPt,
      this.framingDirectionVec,
      this.framingPerpDirectionVec,
      true,
      true,
    );

    const suppressedGaps = this.buildStudSuppressionGaps(openingBands);
    const filteredOrigins = originPoints.filter((origin) => !this.isPerpInAnyGap(origin.perpDistance, suppressedGaps));

    const minPerpDist = sortedVertices[0].perpDistance;
    const maxPerpDist = sortedVertices[sortedVertices.length - 1].perpDistance;

    return this.createStudSegments(
      filteredOrigins,
      perimeter,
      this.framingDirectionVec,
      spacingSign,
      studHalfThickness,
      minPerpDist,
      maxPerpDist,
      studEndTrimCtx,
    );
  }

  private buildStudSuppressionGaps(openingBands: WallOpeningHorizontalBand[]): { minPerp: number; maxPerp: number }[] {
    return openingBands.map((band) => ({
      minPerp: band.combinedPerpExtent.startPerp,
      maxPerp: band.combinedPerpExtent.endPerp,
    }));
  }

  private isPerpInAnyGap(perpDistance: number, gaps: { minPerp: number; maxPerp: number }[]): boolean {
    for (const gap of gaps) {
      if (perpDistance >= gap.minPerp - DIST_EPSILON && perpDistance <= gap.maxPerp + DIST_EPSILON) {
        return true;
      }
    }
    return false;
  }

  private createStudSegments(
    originPoints: FramingSpacingOriginPoint[],
    perimeter: FramingBoundaryWithMetadata,
    framingDirection: Vec2,
    spacingSign: 1 | -1,
    studHalfThickness: number,
    minPerpDist: number,
    maxPerpDist: number,
    studEndTrimCtx: StudEndTrimContext,
  ): GeneratedWallMember[] {
    const generatedStuds: GeneratedWallMember[] = [];

    const slightlyBiggerOuterPoly = offsetPolyLine2(perimeter.polyline, SLIGHTLY_LESS_THAN_HALF * COMPACTNESS_EPSILON);

    const wt1: number = 1.0 - COMPACTNESS_EPSILON;
    const wt2: number = COMPACTNESS_EPSILON;

    const layoutStartPerp = this.projectPerp(this.framingStartPt[0], this.framingStartPt[1]);

    for (const originPoint of originPoints) {
      const isLayoutStart = Math.abs(originPoint.perpDistance - layoutStartPerp) < DIST_EPSILON;
      const isPerimeterEndPerp =
        Math.abs(originPoint.perpDistance - minPerpDist) < DIST_EPSILON ||
        Math.abs(originPoint.perpDistance - maxPerpDist) < DIST_EPSILON;
      const applyCenterlineOffset = !isLayoutStart && !isPerimeterEndPerp;

      const rayX = applyCenterlineOffset
        ? originPoint.x - spacingSign * studHalfThickness * this.framingPerpDirectionVec[0]
        : originPoint.x;
      const rayY = applyCenterlineOffset
        ? originPoint.y - spacingSign * studHalfThickness * this.framingPerpDirectionVec[1]
        : originPoint.y;
      const linePolyInters = intersectRay2PolyLine2(
        perimeter.polyline,
        rayX,
        rayY,
        framingDirection[0],
        framingDirection[1],
      );
      ray2PolyLine2IntersectionMakeUnique(linePolyInters);

      for (let j = 0; j < linePolyInters.length - 1; j++) {
        const startIntersection = linePolyInters[j];
        const endIntersection = linePolyInters[j + 1];
        const { x: x1, y: y1 } = startIntersection;
        const { x: x2, y: y2 } = endIntersection;
        const startHit: StudEndHit = {
          edgeIndex: startIntersection.edgeIndex,
          x: x1,
          y: y1,
        };
        const endHit: StudEndHit = {
          edgeIndex: endIntersection.edgeIndex,
          x: x2,
          y: y2,
        };

        const contBeg = classifyContainmentVec2PolyLine2(
          wt1 * x1 + wt2 * x2,
          wt1 * y1 + wt2 * y2,
          slightlyBiggerOuterPoly,
        );
        const contEnd = classifyContainmentVec2PolyLine2(
          wt1 * x2 + wt2 * x1,
          wt1 * y2 + wt2 * y1,
          slightlyBiggerOuterPoly,
        );

        if (contBeg === Vec2PolyLine2ContainmentClass.INSIDE && contEnd === Vec2PolyLine2ContainmentClass.INSIDE) {
          const rawSegment = polyLine2ByTwoPoints(x1, y1, x2, y2);

          const segment = trimStudSegmentAtPerpendicularPlateEnds(rawSegment, startHit, endHit, studEndTrimCtx);

          if (polyLine2Length(segment) < 2 * MIN_FRAMING_STUD_LENGTH) {
            continue;
          }

          if (this.segmentCoincidentOnAnyPolyEdge(segment[0], segment[1], segment[2], segment[3], perimeter.polyline)) {
            continue;
          }

          generatedStuds.push({
            polyline: segment,
            memberId: this.generateMemberId(),
            profileDescriptor: this.defaultProfileDescriptor,
            framingType: WallFramingType.STANDARD_STUD,
            isBoundary: false,
            boundaryStackIndex: 0,
            depthStackIndex: 0,
            startHit,
            endHit,
          });
        }
      }
    }

    return generatedStuds;
  }

  private projectPerp(x: number, y: number): number {
    const dx = x - this.framingStartPt[0];
    const dy = y - this.framingStartPt[1];
    return dx * this.framingPerpDirectionVec[0] + dy * this.framingPerpDirectionVec[1];
  }

  private computeStructuralMetadata(members: GeneratedWallMember[], studEndTrimCtx: StudEndTrimContext): Framing[] {
    const components: Framing[] = [];
    const elevation = this.input.framingMetadata.elevation ?? 0;
    const framingPlane = planeNewXZ(0, 0, elevation);

    for (const member of members) {
      const profileDescriptor = member.profileDescriptor;
      const rail2D = polyLine2Copy(member.polyline);
      const skipZFightTrim =
        member.framingType === WallFramingType.BLOCKING || member.framingType === WallFramingType.TOP_SILL_PLATE;

      let topPlateClipPlanes: Plane[] | undefined;
      if (
        member.isBoundary &&
        member.framingType === WallFramingType.TOP_PLATE &&
        member.boundaryEdgeIndex !== undefined
      ) {
        const topPlateClip = buildSlopedTopPlateClipPlanes(
          member.boundaryEdgeIndex,
          this.input.perimeter,
          elevation,
          profileDescriptor.height,
        );
        if (topPlateClip.clipPlanes.length > 0) {
          topPlateClipPlanes = topPlateClip.clipPlanes;
          if (topPlateClip.startExtension > DIST_EPSILON) {
            polyLine2Extend(rail2D, topPlateClip.startExtension, 0);
          }
          if (topPlateClip.endExtension > DIST_EPSILON) {
            polyLine2Extend(rail2D, 0, topPlateClip.endExtension);
          }
        }
      }

      if (!skipZFightTrim) {
        polyLine2Extend(rail2D, -DIST_Z_FIGHT, -DIST_Z_FIGHT);
      }
      let rail3D = polyLine2OnPlane(framingPlane, rail2D);
      if (member.isBoundary) {
        rail3D = offsetBoundaryRail3DForExteriorFace(
          rail3D,
          member.framingType,
          profileDescriptor,
          member.boundaryStackIndex,
        );
      } else if (member.framingType === WallFramingType.HEADER) {
        const planDepthOffset = openingHeaderPlanDepthOffset(profileDescriptor.height, member.depthStackIndex);
        if (planDepthOffset > DIST_EPSILON) {
          rail3D = polyLine3Move(rail3D, 0, planDepthOffset, 0);
        }
      }

      let nX: number;
      let nY: number;
      let nZ: number;
      if (member.framingType === WallFramingType.HEADER) {
        const headerNormal = openingHeaderExtrusionNormal();
        nX = headerNormal.nX;
        nY = headerNormal.nY;
        nZ = headerNormal.nZ;
      } else {
        const extrusionNormal = wallFramingExtrusionNormal(rail3D);
        nX = extrusionNormal.nX;
        nY = extrusionNormal.nY;
        nZ = extrusionNormal.nZ;
      }
      let clipPlanes: Plane[] | undefined = undefined;
      if (member.framingType === WallFramingType.STANDARD_STUD && member.startHit && member.endHit) {
        const planes = buildStudEndClipPlanes(member.polyline, member.startHit, member.endHit, studEndTrimCtx);
        if (planes.length > 0) {
          clipPlanes = planes;
        }
      } else if (
        (member.framingType === WallFramingType.KING_STUD || member.framingType === WallFramingType.TOP_CRIPPLE_STUD) &&
        member.endHit
      ) {
        const planes = buildStudEndClipPlanes(member.polyline, member.startHit, member.endHit, studEndTrimCtx);
        if (planes.length > 0) {
          clipPlanes = planes;
        }
      } else if (topPlateClipPlanes) {
        clipPlanes = topPlateClipPlanes;
      }

      // Share one rail id across a header's plies (like the legacy pipeline) so they collapse to a
      // single lumber-rails table entry when the plies are later merged into one output header.
      const railId = member.framingType === WallFramingType.HEADER ? member.headerGroupId : undefined;
      const rail = lumberRailNew(rail3D, nX, nY, nZ, clipPlanes, undefined, undefined, railId);

      components.push({
        id: member.memberId,
        type: member.framingType,
        profileDescriptor,
        rails: [rail],
        meta: member.openingIds ? { openingHandle: undefined, openingIds: member.openingIds } : undefined,
        // Carried through so the output conversion can merge a header's depth-stacked plies into one
        // member (see generatedAssemblyToWallFraming). Dropped when adapting to the output type.
        headerGroupId: member.headerGroupId,
      });
    }

    return components;
  }

  private segmentCoincidentOnAnyPolyEdge(x1: number, y1: number, x2: number, y2: number, pl: PolyLine2): boolean {
    for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
      const edge = polyLine2Edge(pl, i);
      if (
        vec2IsBetweenUsingLengthBasedCalc(edge[0], edge[1], edge[2], edge[3], x1, y1, COMPACTNESS_EPSILON) &&
        vec2IsBetweenUsingLengthBasedCalc(edge[0], edge[1], edge[2], edge[3], x2, y2, COMPACTNESS_EPSILON)
      ) {
        return true;
      }
    }
    return false;
  }
}
